import React, { useCallback, useContext, useEffect, useState } from "react";
import { isNonNullish } from "remeda";

import * as auth0 from "@/auth/auth0";
import {
  getSubscriptionPlan,
  hasPermission,
  isExpired,
  Permission,
  SubscriptionPlan,
} from "@/auth/authorization";
import { env } from "@/env/env";
import { Customer, User } from "@/types/proto/exabel/user/user_messages";

import { AuthLogin } from "./auth-login";

export type AuthContextValue = {
  id?: string;
  isLoading: boolean;
  exabelUserResourceName?: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  fullName?: string;
  createdAt?: string;
  timezone?: string;
  customer?: Customer;
  isAdmin: boolean;
  isExternal: boolean;
  isBlocked: boolean;
  isExpired: boolean;
  exportDashboard: boolean;
  exportSignals: boolean;
  exportSignalsLimited: boolean;
  exportFinancialModels: boolean;
  showDebug: boolean;
  showExperimental: boolean;
  showImportTimeSeries: boolean;
  showManageDataSet: boolean;
  showSupport: boolean;
  showDataCatalog: boolean;
  showExabelLandingPage: boolean;
  showPlotter: boolean;
  showLegacyModels: boolean;
  showData: boolean;
  showJobs: boolean;
  showKpiComparison: boolean;
  writeModel: boolean;
  writeFinancialModel: boolean;
  writeAlphaAnalysis: boolean;
  writePortfolioStrategy: boolean;
  showScreener: boolean;
  writeChart: boolean;
  writeCompanyDashboard: boolean;
  writeCustomerWatchlist: boolean;
  writeDashboard: boolean;
  writeUsers: boolean;
  readWidgetQueue: boolean;
  writeImportJobs: boolean;
  readImportJobs: boolean;
  writeConnection: boolean;
  readExabelApis: boolean;
  readAdcAudit: boolean;
  writePublicSharing: boolean;
  writeVendorAssets: boolean;
  writeAlert: boolean;
  writeKpi: boolean;
  writeKpiHierarchy: boolean;
  readPublicKpi: boolean;
  writePublicKpi: boolean;
  writeDerivedSignal: boolean;
  hasRoles: boolean;
  subscriptionPlan: SubscriptionPlan;
  updateName: (firstName?: string, lastName?: string) => void;
};

export const AuthContext = React.createContext<AuthContextValue | null>(null);

export const AuthProvider: React.FC<{ children?: React.ReactNode }> = (
  props
) => {
  const [user, setUser] = useState<User>();
  const [isLoading, setLoading] = useState(true);

  // Load user profile on page load.
  const load = useCallback(() => {
    setLoading(true);
    auth0
      .fetchUser()
      .then(setUser)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);
  useEffect(load, [load]);

  // Redirect to login when the fetch completes and the user is not
  // authenticated. fetchUser() returns {} on a 401 (distinguishable from
  // a network error which leaves user as undefined).
  // When SHOW_BACKEND_SELECT is true (localhost/PR previews), we skip the
  // auto-redirect and render the backend selector instead.
  const showBackendSelect = env().SHOW_BACKEND_SELECT;
  useEffect(() => {
    if (!isLoading && !user?.auth0Uid && !showBackendSelect) {
      auth0.login();
    }
  }, [isLoading, user, showBackendSelect]);

  // Allow updating of user first/last name
  const updateName = (firstName?: string, lastName?: string) =>
    setUser((prev) =>
      prev ? Object.assign({}, prev, { firstName, lastName }) : prev
    );

  // Don't render the app until auth is resolved and user is authenticated.
  // This prevents a flash of unauthenticated content while the login
  // redirect is pending.
  if (isLoading || !user?.auth0Uid) {
    if (showBackendSelect && !isLoading) {
      return <AuthLogin />;
    }
    return null;
  }

  const value = { ...createValue(user, updateName), isLoading: false };

  return (
    <AuthContext.Provider value={value}>{props.children}</AuthContext.Provider>
  );
};

// Create the AuthContext value for a logged-in user.
const createValue = (
  user: User,
  updateName: (firstName?: string, lastName?: string) => void
): AuthContextValue => {
  const authorization = user.authorization || {};
  const userInfo = {
    id: user.auth0Uid,
    email: user.email,
    fullName: [user.firstName, user.lastName].filter(isNonNullish).join(" "),
    firstName: user.firstName,
    lastName: user.lastName,
    createdAt: user.createdAt,
    customer: user.customer,
    timezone: user.timeZone,
    isExternal: checkIsExternal(user),
    isAdmin: hasPermission(user)(Permission.READ_USER),
    isBlocked: !!user.blocked,
    isExpired: isExpired(user),
    hasRoles: (authorization?.roles?.length ?? 0) > 0,
    subscriptionPlan: getSubscriptionPlan(authorization),
    updateName,
  };

  const exportPermissions = {
    exportDashboard: hasPermission(user)(Permission.EXPORT_DASHBOARD),
    exportSignals: hasPermission(user)(Permission.EXPORT_SIGNALS),
    exportSignalsLimited: hasPermission(user)(
      Permission.EXPORT_SIGNALS_LIMITED
    ),
    exportFinancialModels: hasPermission(user)(
      Permission.EXPORT_FINANCIAL_MODEL
    ),
  };

  const accessPermissions = {
    showExperimental: hasPermission(user)(Permission.ACCESS_EXPERIMENTAL),
    showScreener: hasPermission(user)(Permission.ACCESS_SCREENER),
    showImportTimeSeries: hasPermission(user)(
      Permission.ACCESS_IMPORT_TIME_SERIES
    ),
    showManageDataSet: hasPermission(user)(Permission.ACCESS_MANAGE_DATA_SET),
    showSupport: hasPermission(user)(Permission.ACCESS_SUPPORT),
    showDataCatalog: hasPermission(user)(Permission.ACCESS_DATA_CATALOG),
    showExabelLandingPage: hasPermission(user)(
      Permission.ACCESS_EXABEL_LANDING_PAGE
    ),
    showPlotter: hasPermission(user)(Permission.ACCESS_PLOTTER),
    showLegacyModels: hasPermission(user)(Permission.ACCESS_LEGACY_MODELS),
    showData: hasPermission(user)(Permission.ACCESS_DATA),
    showJobs: hasPermission(user)(Permission.ACCESS_JOBS),
    showKpiComparison: hasPermission(user)(Permission.ACCESS_KPI_COMPARISON),
  };

  const readPermissions = {
    showDebug: hasPermission(user)(Permission.READ_DEBUG),
    readExabelApis: hasPermission(user)(Permission.READ_EXABEL_APIS),
    readImportJobs: hasPermission(user)(Permission.READ_IMPORT_JOBS),
    readPublicKpi: hasPermission(user)(Permission.READ_PUBLIC_KPI),
    readWidgetQueue: hasPermission(user)(Permission.READ_WIDGET_QUEUE),
    readAdcAudit: hasPermission(user)(Permission.READ_ADC_AUDIT),
  };

  const writePermissions = {
    writeAlphaAnalysis: hasPermission(user)(Permission.WRITE_ALPHA_ANALYSIS),
    writeModel: hasPermission(user)(Permission.WRITE_MODEL),
    writeFinancialModel: hasPermission(user)(Permission.WRITE_FINANCIAL_MODEL),
    writePortfolioStrategy: hasPermission(user)(
      Permission.WRITE_PORTFOLIO_STRATEGY
    ),
    writeChart: hasPermission(user)(Permission.WRITE_CHART),
    writeCompanyDashboard: hasPermission(user)(
      Permission.WRITE_COMPANY_DASHBOARD
    ),
    writeCustomerWatchlist: hasPermission(user)(
      Permission.WRITE_CUSTOMER_WATCHLIST
    ),
    writeDashboard: hasPermission(user)(Permission.WRITE_DASHBOARD),
    writeUsers: hasPermission(user)(Permission.WRITE_USERS),
    writeImportJobs: hasPermission(user)(Permission.WRITE_IMPORT_JOBS),
    writeConnection: hasPermission(user)(Permission.WRITE_CONNECTION),
    writePublicSharing: hasPermission(user)(Permission.WRITE_PUBLIC_SHARING),
    writeAlert: hasPermission(user)(Permission.WRITE_ALERT),
    writeVendorAssets: hasPermission(user)(Permission.WRITE_VENDOR_ASSETS),
    writeKpi: hasPermission(user)(Permission.WRITE_KPI),
    writeKpiHierarchy: hasPermission(user)(Permission.WRITE_KPI_HIERARCHY),
    writePublicKpi: hasPermission(user)(Permission.WRITE_PUBLIC_KPI),
    writeDerivedSignal: hasPermission(user)(Permission.WRITE_DERIVED_SIGNAL),
  };

  return {
    ...userInfo,
    ...exportPermissions,
    ...accessPermissions,
    ...readPermissions,
    ...writePermissions,
    exabelUserResourceName: user.name,
    isLoading: false,
  };
};

// Check if the user is an external user.
const checkIsExternal = (user: User) => {
  return (
    !hasPermission(user)(Permission.READ_USER) &&
    Boolean(user.email) &&
    !user.email?.endsWith("@exabel.com")
  );
};

export const useAuthContext = () => {
  const context = useContext(AuthContext);
  if (!context)
    throw new Error(
      "useAuthContext can only be used in parts of the app where a user is guaranteed to exists"
    );
  return context;
};
